import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Firebase 에러 메시지 한글화
const getFirebaseErrorMessage = (error: any): string => {
  const errorCode = error?.code || '';
  
  switch (errorCode) {
    case 'auth/invalid-credential':
      return '이메일 또는 비밀번호가 일치하지 않습니다.';
    case 'auth/wrong-password':
      return '비밀번호가 일치하지 않습니다.';
    case 'auth/user-not-found':
      return '존재하지 않는 사용자입니다.';
    case 'auth/too-many-requests':
      return '로그인 시도가 너무 많습니다. 잠시 후 다시 시도해주세요.';
    case 'auth/network-request-failed':
      return '네트워크 연결을 확인해주세요.';
    case 'auth/user-disabled':
      return '비활성화된 계정입니다. 관리자에게 문의하세요.';
    default:
      return '로그인에 실패했습니다. 다시 시도해주세요.';
  }
};

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const theme = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError('이메일과 비밀번호를 입력해 주세요.');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await login({ email, password });
      // PublicRoute가 자동으로 /dashboard로 리다이렉트
    } catch (err: any) {
      const errorMessage = getFirebaseErrorMessage(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        px: { xs: 2, md: 4 },
        py: { xs: 8, md: 10 },
        backgroundImage:
          'radial-gradient(circle at 18% 18%, rgba(90, 125, 255, 0.18), transparent 55%), radial-gradient(circle at 80% 12%, rgba(125, 159, 255, 0.22), transparent 45%), linear-gradient(160deg, #101d3c 0%, #142750 42%, #1f3e7f 100%)',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 420,
          borderRadius: 4,
          p: { xs: 3.5, md: 4 },
          border: `1px solid ${alpha(theme.palette.primary.light, 0.28)}`,
          backgroundImage: `linear-gradient(155deg, ${alpha(theme.palette.background.paper, 0.94)}, ${alpha(
            theme.palette.primary.dark,
            0.3,
          )})`,
          boxShadow: `0 28px 48px ${alpha(theme.palette.primary.dark, 0.3)}`,
          backdropFilter: 'blur(16px)',
        }}
      >
        <Stack spacing={0.75} textAlign="center" mb={3}>
          <Typography
            variant="overline"
            sx={{ letterSpacing: '0.3em', color: alpha(theme.palette.text.secondary, 0.85) }}
          >
            MOLDCARE - 스마트 금형 자산관리 
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
            EGMMS V3
          </Typography>
          <Typography variant="body2" sx={{ color: alpha(theme.palette.text.secondary, 0.85) }}>
            인증 정보를 입력하고 실시간 프로젝트 현황을 확인하세요.
          </Typography>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <TextField
            required
            fullWidth
            id="email"
            label="이메일"
            name="email"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            required
            fullWidth
            id="password"
            name="password"
            label="비밀번호"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              mt: 1,
              py: 1.25,
              fontWeight: 700,
              letterSpacing: '0.08em',
            }}
          >
            {loading ? '로그인 중...' : '로그인'}
          </Button>
          <Stack direction="row" justifyContent="center" spacing={1} mt={0.5}>
            <Typography variant="body2" color={alpha(theme.palette.text.secondary, 0.8)}>
              처음 방문하셨나요?
            </Typography>
            <Link
              component={RouterLink}
              to="/register"
              variant="body2"
              sx={{ fontWeight: 600, color: theme.palette.secondary.light }}
            >
              회원 가입
            </Link>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;